import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axiosInstance from '../../axiosConfig';
import GlassPaper from '../GlassPaper';
import './ScreenerCard.css';

function formatPrice(value) {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return '-';
    }
    return value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatPercent(value) {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return '-';
    }
    return `${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function formatSignedPercent(value) {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return '-';
    }
    const sign = value > 0 ? '+' : value < 0 ? '-' : '';
    return `${sign}${Math.abs(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function formatSignedCurrency(value) {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return '-';
    }
    const sign = value > 0 ? '+' : value < 0 ? '-' : '';
    return `${sign}₹${Math.abs(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value) {
    if (!value) {
        return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

const DEFAULT_STATE = {
    loading: true,
    data: null,
    error: null,
    lastUpdated: null,
};

function truncateReason(reason, max = 80) {
    if (!reason || typeof reason !== 'string') {
        return '';
    }
    if (reason.length <= max) {
        return reason;
    }
    return `${reason.slice(0, max - 3)}...`;
}

function ScreenerCard({ id, title, description, endpoint }) {
    const [state, setState] = useState(DEFAULT_STATE);
    const mountedRef = useRef(true);
    const followUpTimerRef = useRef(null);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (followUpTimerRef.current) {
                clearTimeout(followUpTimerRef.current);
                followUpTimerRef.current = null;
            }
        };
    }, []);

    const loadData = useCallback(async (forceRefresh = false) => {
        if (!mountedRef.current) {
            return;
        }
        setState((prev) => ({ ...prev, loading: true, error: null }));
        try {
            const response = await axiosInstance.get(endpoint, {
                params: forceRefresh ? { forceRefresh: true } : undefined,
            });
            if (!mountedRef.current) {
                return;
            }
            setState({
                loading: false,
                data: response.data,
                error: null,
                lastUpdated: new Date(),
            });
            if (forceRefresh) {
                if (followUpTimerRef.current) {
                    clearTimeout(followUpTimerRef.current);
                }
                followUpTimerRef.current = setTimeout(() => {
                    if (!mountedRef.current) {
                        return;
                    }
                    loadData(false);
                }, 1500);
            }
        } catch (err) {
            console.error(`Failed to load screener ${id}`, err);
            if (!mountedRef.current) {
                return;
            }
            setState({
                loading: false,
                data: null,
                error: err.response?.data?.detail || err.message || 'Unknown error',
                lastUpdated: null,
            });
        }
    }, [endpoint, id]);

    useEffect(() => {
        loadData(false);
    }, [loadData]);

    const handleRefresh = useCallback(() => loadData(true), [loadData]);

    const { loading, data, error, lastUpdated } = state;

    const meta = data?.meta;
    const isPending = Boolean(meta?.pending);
    const results = data?.results || [];
    const failureCount = meta?.failureCount || 0;
    const summaryItems = useMemo(() => {
        const generatedValue = meta?.pending
            ? 'Preparing...'
            : meta?.generatedAt
                ? new Date(meta.generatedAt).toLocaleString('en-IN')
                : 'N/A';
        const sourceValue = meta?.pending ? 'Warming up' : meta?.cached ? 'Cache' : 'Live';
        const qualifiedValue = meta?.pending
            ? 'N/A'
            : meta
                ? `${meta.qualified ?? results.length}/${meta.totalConstituents ?? 'N/A'}`
                : 'N/A';
        return [
            { label: 'Status', value: meta?.pending ? 'Preparing' : 'Ready' },
            { label: 'Generated', value: generatedValue },
            { label: 'Source', value: sourceValue },
            { label: 'Qualified', value: qualifiedValue },
            { label: 'Tolerance', value: meta?.tolerancePct != null ? formatPercent(meta.tolerancePct) : 'N/A' },
            { label: 'Failures', value: failureCount },
        ];
    }, [meta, results.length, failureCount]);

    const failures = data?.failures || [];
    const failurePreview = useMemo(() => {
        if (!failures.length) {
            return '';
        }
        const slice = failures.slice(0, 5);
        const remainder = failures.length - slice.length;
        const list = slice
            .map((entry) => {
                const reason = entry.reason ? ` (${truncateReason(entry.reason)})` : '';
                return `${entry.symbol}${reason}`;
            })
            .join(', ');
        return remainder > 0 ? `${list}, +${remainder} more` : list;
    }, [failures]);

    const failureTooltip = useMemo(() => {
        if (!failures.length) {
            return undefined;
        }
        return failures
            .map((entry) => {
                const reason = entry.reason ? ` :: ${entry.reason}` : '';
                return `${entry.symbol}${reason}`;
            })
            .join('\n');
    }, [failures]);

    useEffect(() => {
        if (!loading && isPending) {
            const timer = setTimeout(() => {
                loadData(false);
            }, 4000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [loading, isPending, loadData]);

    return (
        <GlassPaper className="screener-card" elevation={0} square={false}>
            <div className="screener-card__header">
                <div className="screener-card__title">
                    <h5>{title}</h5>
                    <p>{description}</p>
                </div>
                <div className="screener-card__actions">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleRefresh} disabled={loading}>
                        {loading ? 'Loading...' : 'Refresh'}
                    </button>
                    {lastUpdated && (
                        <div className="screener-card__timestamp">
                            Last checked {lastUpdated.toLocaleTimeString('en-IN')}
                        </div>
                    )}
                </div>
            </div>
            <div className="screener-card__body">
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                {!loading && isPending && !error && (
                    <div className="alert alert-info" role="alert">
                        Preparing the latest screener snapshot. This can take up to half a minute on the first run.
                    </div>
                )}

                {!loading && meta?.refreshError && !isPending && (
                    <div className="alert alert-warning" role="alert">
                        Latest refresh hit an issue: {meta.refreshError}. Showing the previous snapshot.
                    </div>
                )}

                <div className="row g-3">
                    {summaryItems.map((item) => (
                        <div className="col-md-2 col-sm-4 col-6" key={`${id}-${item.label}`}>
                            <div className="screener-meta-tile">
                                <div className="screener-meta-label">{item.label}</div>
                                <div className="screener-meta-value">{item.value}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="table-responsive mt-3">
                    <table className="table table-sm table-striped align-middle">
                        <thead>
                            <tr>
                                <th scope="col">Rank</th>
                                <th scope="col">Symbol</th>
                                <th scope="col">New High</th>
                                <th scope="col">Prev High</th>
                                <th scope="col">Prev High Date</th>
                                <th scope="col">Delta</th>
                                <th scope="col">Delta %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!loading && isPending && (
                                <tr>
                                    <td colSpan={7} className="text-center text-muted">
                                        Warming up screener data...
                                    </td>
                                </tr>
                            )}
                            {!loading && !isPending && results.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="text-center text-muted">
                                        No symbols are within the configured tolerance right now.
                                    </td>
                                </tr>
                            )}
                            {results.map((row) => (
                                <tr key={`${row.rank}-${row.symbol}`} className={row.breached ? 'table-success' : ''}>
                                    <td>{row.rank}</td>
                                    <td>
                                        <div className="fw-semibold">{row.symbol}</div>
                                    </td>
                                    <td>₹{formatPrice(row.newHigh)}</td>
                                    <td>₹{formatPrice(row.previousHigh)}</td>
                                    <td>{formatDate(row.previousHighDate)}</td>
                                    <td>{formatSignedCurrency(row.delta)}</td>
                                    <td>{formatSignedPercent(row.deltaPct)}</td>
                                </tr>
                            ))}
                            {loading && (
                                <tr>
                                    <td colSpan={7} className="text-center text-muted">
                                        Loading data...
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {failures.length > 0 && (
                    <div className="alert alert-warning mt-3" role="alert" title={failureTooltip}>
                        Unable to evaluate {failures.length} symbols: {failurePreview}
                    </div>
                )}
            </div>
        </GlassPaper>
    );
}

ScreenerCard.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    endpoint: PropTypes.string.isRequired,
};

export default ScreenerCard;
